import { useQueryClient } from "@tanstack/react-query";
import { deleteFileFromStorage } from "../services/deleteFile";

/**
 * Custom hook to delete a file from storage and update the UI using React Query.
 * @returns {Object} - { deleteFile: async function to delete a file }
 */
export default function useDeleteFile() {
  const queryClient = useQueryClient();

  /**
   * Deletes a file and updates the categoryFolders query cache.
   * @param {Object} file - The file object to delete. Must include storagePath.
   * @returns {Promise<void>}
   */
  async function deleteFile(file) {
    if (!file || !file.storagePath) {
      throw new Error("File with valid storagePath is required for deletion.");
    }

    try {
      // Delete file from storage
      await deleteFileFromStorage(file.storagePath);

      // Update the cached categoryFolders query to remove the deleted file
      queryClient.setQueryData(["categoryFolders"], (oldData) => {
        if (!oldData) return oldData;

        // Map through folders and remove the file from files array
        return oldData.map((folder) => {
          if (!folder.files) return folder;
          const filteredFiles = folder.files.filter(
            (f) => f.storagePath !== file.storagePath
          );
          if (filteredFiles.length === folder.files.length) {
            // No change for this folder
            return folder;
          }
          return {
            ...folder,
            files: filteredFiles,
          };
        });
      });

      // Optionally, invalidate queries to refetch fresh data if needed
      // await queryClient.invalidateQueries(["categoryFolders"]);
    } catch (error) {
      console.error("Failed to delete file:", error);
      throw error;
    }
  }

  return { deleteFile };
}
